import { useCallback } from 'react'
import { ANSWER_OUTCOME } from '@trustero/trustero-api-web/lib/tprmquestionnaire/questionnaire_internal_pb'
import { ANSWER_JUDGMENT } from '@trustero/trustero-api-web/lib/tprmquestionnaire/tprmquestionnaire_pb'
import { FilterParam } from 'src/components/Reusable/IndexPage/FilterBar/FilterBar.types'
import { useSetQuickSearchQuery } from 'src/Utils/search/quickSearch'
import {
  QuestionnaireCommentItem,
  QuestionnaireConditionRule,
  QuestionnaireQuestion,
  QuestionnaireSection,
} from './QuestionnaireTypes'
import { QuestionnaireAttachmentIndexItem } from './QuestionnaireAttachmentsPanel'
import { activeSupplementals } from './QuestionnaireSupplementalInputs'

/**
 * Checks if a questionnaire section or question is visible based on conditional gating rules.
 * No dependsOn rules means always visible; OR'd rules mean visible once at least one
 * rule's depended-on question's current answer equals its matchValue.
 */
export const isQuestionnaireItemVisible = (
  dependsOn: QuestionnaireConditionRule[] = [],
  answers: Record<string, string> = {},
): boolean => {
  if (dependsOn.length === 0) {
    return true
  }
  return dependsOn.some(
    (rule) => answers[rule.dependsOnQuestionId] === rule.matchValue,
  )
}

/**
 * A question's label: its position in its section, and its own number when it
 * carries one -- the original's number on a customized questionnaire ("3.12"),
 * or an imported questionnaire's key ("I.3").
 */
export const questionNumberLabel = (
  questionNumber: number,
  sourceKey?: string,
): string =>
  sourceKey?.trim()
    ? `Question # ${questionNumber} · ${sourceKey.trim()}`
    : `Question # ${questionNumber}`

/**
 * Counts how many questions in a section have been answered with non-empty values.
 */
export const getSectionAnsweredCount = (
  section: QuestionnaireSection,
  answers: Record<string, string>,
): number =>
  section.questionsList.filter((question) =>
    Boolean(answers[question.questionId]?.trim()),
  ).length

/**
 * Calculates completion percentage between 0 and 100.
 */
export const calculateProgress = (answered: number, total: number): number => {
  if (total <= 0) return 0
  return Math.round((answered / total) * 100)
}

/**
 * Finds the sections either side of the selected one, for stepping through a
 * questionnaire section by section.
 *
 * Pass the *visible* sections: a section gated off by an earlier answer isn't
 * offered in the sidebar, so stepping into it would strand the reader
 * somewhere they can't navigate back to.
 *
 * Either side is undefined at the ends, or when the selected id isn't in the
 * list at all (it was just gated away by a change of answer).
 */
export const getAdjacentSections = (
  sections: QuestionnaireSection[],
  selectedSectionId: string,
): {
  previousSection?: QuestionnaireSection
  nextSection?: QuestionnaireSection
} => {
  const index = sections.findIndex(
    (section) => section.sectionId === selectedSectionId,
  )
  if (index === -1) {
    return {}
  }
  return {
    previousSection: index > 0 ? sections[index - 1] : undefined,
    nextSection: index < sections.length - 1 ? sections[index + 1] : undefined,
  }
}

/**
 * Answered and total question counts for one section, excluding questions
 * hidden by a conditional rule -- so "2 of 3 answered" always matches what the
 * reader can see on screen.
 */
/**
 * Whether a question counts as answered for progress and section counts.
 *
 * A FileUpload question is answered with a document, not text: the upload path
 * creates its answer row with an empty value deliberately, so counting only
 * non-blank values left a question with a file attached permanently showing as
 * unanswered and the progress bar unable to reach 100%.
 */
export const isQuestionAnswered = (
  question: QuestionnaireQuestion,
  answers: Record<string, string>,
): boolean => {
  if (question.responseType === 'FileUpload') {
    return (question.attachmentsList?.length ?? 0) > 0
  }
  return Boolean(answers[question.questionId]?.trim())
}

// The question filters' Status options. Every question is in exactly one of
// the first four buckets: a Trustero Intelligence outcome short of a full
// answer wins, since it says more than whether there is text; otherwise
// answered or not. NEEDS_REVIEW overlaps them: a flagged answer is also
// answered, partial or unable (questionStatuses).
export enum QUESTION_ANSWER_STATUS {
  UNANSWERED = 'unanswered',
  ANSWERED = 'answered',
  PARTIAL = 'partial',
  UNABLE = 'unable',
  NEEDS_REVIEW = 'needs_review',
}

// The question's bucket: never NEEDS_REVIEW.
export const questionAnswerStatus = (
  question: QuestionnaireQuestion,
  answers: Record<string, string>,
): QUESTION_ANSWER_STATUS => {
  if (question.answerOutcome === ANSWER_OUTCOME.ANSWER_OUTCOME_PARTIAL) {
    return QUESTION_ANSWER_STATUS.PARTIAL
  }
  if (question.answerOutcome === ANSWER_OUTCOME.ANSWER_OUTCOME_UNANSWERABLE) {
    return QUESTION_ANSWER_STATUS.UNABLE
  }
  return isQuestionAnswered(question, answers)
    ? QUESTION_ANSWER_STATUS.ANSWERED
    : QUESTION_ANSWER_STATUS.UNANSWERED
}

// Every Status option the question matches: its bucket, and Needs review when
// a run flagged it.
export const questionStatuses = (
  question: QuestionnaireQuestion,
  answers: Record<string, string>,
): QUESTION_ANSWER_STATUS[] => {
  const bucket = questionAnswerStatus(question, answers)
  return question.needsAttention
    ? [bucket, QUESTION_ANSWER_STATUS.NEEDS_REVIEW]
    : [bucket]
}

// The question filters' Judgment options: what grading made of an answer (a
// reviewer's own over it), and the two states of one it has not judged.
export enum QUESTION_JUDGMENT {
  CONTRADICTED = 'contradicted',
  INCOMPLETE = 'incomplete',
  BELOW_STANDARD = 'below_standard',
  PARTIALLY_MEETS = 'partially_meets',
  MEETS = 'meets',
  NOT_APPLICABLE = 'not_applicable',
  NOT_ANSWERED = 'not_answered',
  NOT_JUDGED = 'not_judged',
}

const JUDGMENT_OF: Partial<Record<ANSWER_JUDGMENT, QUESTION_JUDGMENT>> = {
  [ANSWER_JUDGMENT.ANSWER_JUDGMENT_CONTRADICTED]:
    QUESTION_JUDGMENT.CONTRADICTED,
  [ANSWER_JUDGMENT.ANSWER_JUDGMENT_INCOMPLETE]: QUESTION_JUDGMENT.INCOMPLETE,
  [ANSWER_JUDGMENT.ANSWER_JUDGMENT_BELOW_STANDARD]:
    QUESTION_JUDGMENT.BELOW_STANDARD,
  [ANSWER_JUDGMENT.ANSWER_JUDGMENT_PARTIALLY_MEETS]:
    QUESTION_JUDGMENT.PARTIALLY_MEETS,
  [ANSWER_JUDGMENT.ANSWER_JUDGMENT_MEETS]: QUESTION_JUDGMENT.MEETS,
  [ANSWER_JUDGMENT.ANSWER_JUDGMENT_NOT_APPLICABLE]:
    QUESTION_JUDGMENT.NOT_APPLICABLE,
  [ANSWER_JUDGMENT.ANSWER_JUDGMENT_NOT_ANSWERED]:
    QUESTION_JUDGMENT.NOT_ANSWERED,
}

// The judgment the badge shows -- a reviewer's override over grading's -- or,
// for an answer grading has not judged, whether there is an answer to judge.
export const questionJudgment = (
  question: QuestionnaireQuestion,
  answers: Record<string, string>,
): QUESTION_JUDGMENT =>
  JUDGMENT_OF[question.judgmentOverride || question.judgment || 0] ??
  (isQuestionAnswered(question, answers)
    ? QUESTION_JUDGMENT.NOT_JUDGED
    : QUESTION_JUDGMENT.NOT_ANSWERED)

// The question filters' Contains options.
export enum QUESTION_CONTAINS {
  COMMENTS = 'comments',
  ATTACHMENTS = 'attachments',
}

// On a questionnaire whose sender pre-answered some questions, the answer
// view shows one of two sets at a time: the questions needing an answer (the
// default), or the other, pre-answered, ones.
export enum QUESTION_VIEW {
  NEEDS_ANSWER = 'needs_answer',
  OTHER = 'other',
}

export const questionView = (question: QuestionnaireQuestion): QUESTION_VIEW =>
  question.preAnswered ? QUESTION_VIEW.OTHER : QUESTION_VIEW.NEEDS_ANSWER

// Every Contains option the question matches. `comments` are its own threads'
// comments (commentsByQuestionId).
export const questionContains = (
  question: QuestionnaireQuestion,
  comments: string[],
): QUESTION_CONTAINS[] => [
  ...(comments.length > 0 ? [QUESTION_CONTAINS.COMMENTS] : []),
  ...((question.attachmentsList?.length ?? 0) > 0
    ? [QUESTION_CONTAINS.ATTACHMENTS]
    : []),
]

// Comment bodies by the id of the question they are on. A comment is filed
// under its question's 1-based position in the FULL section and question
// lists, so `sections` must be those, never a gated or filtered subset.
// Section- and questionnaire-level comments (question 0) are on no question.
export const commentsByQuestionId = (
  sections: QuestionnaireSection[],
  comments: QuestionnaireCommentItem[],
): Map<string, string[]> => {
  const out = new Map<string, string[]>()
  comments.forEach((comment) => {
    const question =
      sections[(comment.sectionNumber || 0) - 1]?.questionsList[
        (comment.questionNumber || 0) - 1
      ]
    if (!question) return
    out.set(question.questionId, [
      ...(out.get(question.questionId) ?? []),
      comment.body,
    ])
  })
  return out
}

export interface QuestionFilters {
  statuses: string[]
  judgments: string[]
  contains: string[]
}

// The question filters' URL params: bracketed as the FilterBar writes them,
// and bare, as a hand-written link may have them -- both are read. Plus the
// FilterBar's search box.
const QUESTION_FILTER_PARAMS = [
  ...[
    FilterParam.QUESTION_ANSWER_STATUS,
    FilterParam.QUESTION_JUDGMENT,
    FilterParam.QUESTION_CONTAINS,
  ].flatMap((param) => [param, `${param}[]`]),
  'search',
]

// landOn: the question a jump goes to. The filters are dropped either way;
// with a question to land on, the view is set to the one it is in, or a jump
// to a pre-answered question lands nowhere. Without one the view stays.
export const dropQuestionFilters = (
  params: URLSearchParams,
  landOn?: QuestionnaireQuestion,
): void => {
  QUESTION_FILTER_PARAMS.forEach((param) => params.delete(param))
  if (landOn) params.set(FilterParam.QUESTION_VIEW, questionView(landOn))
}

// dropQuestionFilters, and the text typed in the search box and not yet
// searched, which filters too but lives outside the URL. Every clear of the
// question filters goes through this, or typed text keeps hiding questions.
export const useDropQuestionFilters = (): ((
  params: URLSearchParams,
  landOn?: QuestionnaireQuestion,
) => void) => {
  const setQuickQuery = useSetQuickSearchQuery()
  return useCallback(
    (params, landOn) => {
      dropQuestionFilters(params, landOn)
      setQuickQuery(null)
    },
    [setQuickQuery],
  )
}

// Within a filter any chosen value matches; across filters, all must. An
// empty filter matches everything. `comments` as for questionContains.
export const questionMatchesFilters = (
  question: QuestionnaireQuestion,
  answers: Record<string, string>,
  { statuses, judgments, contains }: QuestionFilters,
  comments: string[] = [],
): boolean =>
  (statuses.length === 0 ||
    questionStatuses(question, answers).some((status) =>
      statuses.includes(status),
    )) &&
  (judgments.length === 0 ||
    judgments.includes(questionJudgment(question, answers))) &&
  (contains.length === 0 ||
    questionContains(question, comments).some((option) =>
      contains.includes(option),
    ))

/**
 * `skipPreAnswered` is for the answering surfaces: the recipient was not asked
 * a pre-answered question. The customer's own view counts every question.
 */
export const getSectionCounts = (
  section: QuestionnaireSection,
  answers: Record<string, string>,
  { skipPreAnswered = false }: { skipPreAnswered?: boolean } = {},
): { answered: number; total: number } => {
  const visible = section.questionsList.filter(
    (question) =>
      !(skipPreAnswered && question.preAnswered) &&
      isQuestionnaireItemVisible(question.dependsOnList, answers),
  )
  return {
    answered: visible.filter((question) =>
      isQuestionAnswered(question, answers),
    ).length,
    total: visible.length,
  }
}

/**
 * Progress across a whole questionnaire, for the answering view's progress bar.
 *
 * Counts only what the reader can actually see and answer: sections and
 * questions hidden by a conditional rule, and questions pre-answered by the
 * sender, are excluded from both figures.
 * Including them would leave the bar unable to reach 100% whenever an answer
 * gates something away.
 *
 * Pass every section, gated or not -- section visibility is evaluated here.
 */
export const getQuestionnaireProgress = (
  sections: QuestionnaireSection[],
  answers: Record<string, string>,
): { answeredCount: number; totalCount: number; percentComplete: number } => {
  let answeredCount = 0
  let totalCount = 0

  sections
    .filter((section) =>
      isQuestionnaireItemVisible(section.dependsOnList, answers),
    )
    .forEach((section) => {
      section.questionsList
        .filter(
          (question) =>
            !question.preAnswered &&
            isQuestionnaireItemVisible(question.dependsOnList, answers),
        )
        .forEach((question) => {
          totalCount += 1
          if (isQuestionAnswered(question, answers)) {
            answeredCount += 1
          }
        })
    })

  return {
    answeredCount,
    totalCount,
    percentComplete: calculateProgress(answeredCount, totalCount),
  }
}

/**
 * The answering view's attachment index, derived from the question tree.
 *
 * Deliberately NOT the ListReceivedVendorQuestionnaireAttachments RPC, which
 * serves the latest SUBMITTED snapshot: that is right for the show page's
 * table, but on the answering view it meant that once a questionnaire had been
 * submitted, the tab showed the previous version forever -- newly attached
 * files never appeared and detached ones stayed listed while editing the next
 * version.
 *
 * The question tree already carries the working form's attachments, so this
 * index and the chips on the questions cannot disagree.
 */
export const attachmentsIndexFromSections = (
  sections: QuestionnaireSection[],
): QuestionnaireAttachmentIndexItem[] =>
  sections.flatMap((section, sectionIndex) =>
    section.questionsList.flatMap((question, questionIndex) =>
      (question.attachmentsList ?? []).map((attachment) => ({
        attachmentId: attachment.attachmentId,
        contentId: attachment.contentId,
        fileName: attachment.fileName,
        mimeType: attachment.mimeType,
        sectionNumber: sectionIndex + 1,
        sectionName: section.name,
        questionNumber: questionIndex + 1,
        sourceKey: question.sourceKey,
        questionId: question.questionId,
        questionText: question.text,
        uploadedAtMs: attachment.uploadedAtMs ?? 0,
      })),
    ),
  )

/**
 * Which visible questions are missing something the template marked required.
 *
 * Reported, never enforced: a vendor who cannot produce a document must still
 * be able to submit, so this drives a "here is what is incomplete" prompt
 * rather than a block. Only REQUIRED supplementals are listed -- an unanswered
 * question is already conveyed by the progress bar and the per-section counts.
 *
 * Visibility matters: a required field inside a section or question that a
 * conditional has gated away is not missing, it is irrelevant.
 */
export interface IncompleteQuestion {
  sectionNumber: number
  sectionName: string
  questionNumber: number
  sourceKey?: string
  questionText: string
  // What is outstanding, by the label the responder saw.
  missing: string[]
}

export const findIncompleteQuestions = (
  sections: QuestionnaireSection[],
  answers: Record<string, string>,
): IncompleteQuestion[] => {
  const out: IncompleteQuestion[] = []

  // Numbered over every section, shown or not: a section's number is its
  // position, the same coordinate the page and its comments use.
  sections.forEach((section, sectionIndex) => {
    if (!isQuestionnaireItemVisible(section.dependsOnList, answers)) return
    section.questionsList.forEach((question, questionIndex) => {
      if (!isQuestionnaireItemVisible(question.dependsOnList, answers)) return
      // Pre-answered: it takes no answer here, so nothing on it is missing.
      if (question.preAnswered) return

      const selected = (answers[question.questionId] ?? '')
        .split(',')
        .map((v) => v.trim())
        .filter(Boolean)

      const missing = activeSupplementals(question, selected)
        .filter((slot) => slot.required)
        .filter((slot) =>
          slot.kind === 'Attachment'
            ? (question.attachmentsList?.length ?? 0) === 0
            : !question.supplementalText?.trim(),
        )
        .map(
          (slot) =>
            slot.label ||
            (slot.kind === 'Attachment'
              ? 'Attach document'
              : 'Additional details'),
        )

      if (missing.length > 0) {
        out.push({
          sectionNumber: sectionIndex + 1,
          sectionName: section.name,
          questionNumber: questionIndex + 1,
          sourceKey: question.sourceKey,
          questionText: question.text,
          missing,
        })
      }
    })
  })

  return out
}

// countQuestionnaireAnswers counts what the reader can actually see: questions
// gated off by their conditions are excluded from both halves, so "N of M"
// agrees with the questions rendered beside it. Previously each preview modal
// counted the whole tree while rendering only the visible part, which is how a
// preview came to read "3 of 9" next to a page saying 67% complete.
export const countQuestionnaireAnswers = (
  sections: QuestionnaireSection[] | undefined,
  answers: Record<string, string>,
  isVisibilityGated = true,
): { answered: number; total: number } => {
  let answered = 0
  let total = 0

  sections
    ?.filter((section) =>
      isVisibilityGated
        ? isQuestionnaireItemVisible(section.dependsOnList, answers)
        : true,
    )
    .forEach((section) => {
      section.questionsList
        .filter((question) =>
          isVisibilityGated
            ? isQuestionnaireItemVisible(question.dependsOnList, answers)
            : true,
        )
        .forEach((question) => {
          total += 1
          // The same rule the progress bar uses, so the preview's count cannot
          // disagree with the page's: a FileUpload question is answered by its
          // attachment, not by answer text.
          if (isQuestionAnswered(question, answers)) {
            answered += 1
          }
        })
    })

  return { answered, total }
}

/**
 * A section's questions as the reader sees them: numbered by position in the
 * FULL question list, then filtered to the visible ones.
 *
 * The order matters and is the same one QuestionnaireQuestionsPanel uses. A
 * question's number is the coordinate its comments are filed under and the one
 * its anchor is built from, so gating an earlier question away must not
 * renumber the rest. Visible numbers can show gaps; that is correct.
 */
export const visibleQuestionsWithNumbers = (
  section: QuestionnaireSection,
  answers: Record<string, string>,
): { question: QuestionnaireQuestion; questionNumber: number }[] =>
  section.questionsList
    .map((question, idx) => ({ question, questionNumber: idx + 1 }))
    .filter(({ question }) =>
      isQuestionnaireItemVisible(question.dependsOnList, answers),
    )

/**
 * The DOM id of a question in the document column. A question is addressed by
 * position, the same coordinate comments use, so the id survives a submit --
 * which recreates every section and question with fresh ids.
 */
export const questionAnchorId = (
  sectionNumber: number,
  questionNumber: number,
): string => `q-${sectionNumber}-${questionNumber}`

/** The DOM id of a thread in the comments rail. Mirrors questionAnchorId. */
export const commentAnchorId = (
  sectionNumber: number,
  questionNumber: number,
): string => `c-${sectionNumber}-${questionNumber}`

/**
 * The reply box at the foot of one thread in the comments rail.
 *
 * Separate from the thread's own anchor because they want different things:
 * the thread anchor identifies the conversation, while this is what has to be
 * ON SCREEN after a thread is selected -- reading a reply you cannot answer is
 * half the job.
 */
export const commentComposerAnchorId = (
  sectionNumber: number,
  questionNumber: number,
): string => `cc-${sectionNumber}-${questionNumber}`

/**
 * Identifies one comment thread within one questionnaire request. Used to track
 * which threads this view has already marked read, so opening one issues
 * exactly one mark rather than one per render.
 */
export const commentThreadKey = (
  requestId: string,
  sectionNumber: number,
  questionNumber: number,
): string => `${requestId}:${sectionNumber}:${questionNumber}`

/**
 * Brings a node into view, smoothly, unless the reader has asked for less
 * motion.
 *
 * Smooth is the point here: both columns scroll themselves when a comment
 * thread is selected, and an instant jump gives no sense of having travelled --
 * you simply find yourself somewhere else and have to re-read the screen to
 * work out where. The animation is what carries "you moved, and this is where
 * to".
 *
 * Shared by the rail and the document column so their motion cannot drift
 * apart, and so the reduced-motion check exists in one place rather than two.
 */
export const scrollAnchorIntoView = (
  node: HTMLElement | null,
  block: ScrollLogicalPosition,
): void => {
  // Guarded: an unimplemented scrollIntoView (jsdom, older embedded views)
  // would throw and take the caller's render with it, which is a steep price
  // for not scrolling.
  if (typeof node?.scrollIntoView !== 'function') return

  // matchMedia is absent in jsdom, so its absence means "no stated preference"
  // rather than a crash.
  const prefersReducedMotion =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches

  node.scrollIntoView({
    block,
    behavior: prefersReducedMotion ? 'auto' : 'smooth',
  })
}
